import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { isCrmReferrerViewEnabled } from "../lib/crmInvestors";
import { toast } from "sonner";
import PortfolioGrowthHero from "../components/PortfolioGrowthHero";
import MyVCCard from "../components/MyVCCard";
import MyIntroducteesCard from "../components/MyIntroducteesCard";
import {
  Users,
  Copy,
  QrCode,
  CheckCircle2,
  Network,
  Layers
} from "lucide-react";

const ReferrerDashboard = () => {
  const { user } = useAuth();
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
    // `fetchData` is a stable in-component fn that re-reads `user.id` from
    // closure when called; intentional manual dep on `user?.id` only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchData = async () => {
    if (!user?.id) return;
    
    try {
      const networkRes = await api.get(`/referrals/network/${user.id}`);
      setNetwork(networkRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(user?.referral_token || "");
    setCopied(true);
    toast.success("Referral code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <DashboardLayout title="Referrer Dashboard" subtitle="Loading...">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={`ref-skeleton-${i}`} className="h-32 bg-surface rounded"></div>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Referrer Dashboard"
      subtitle="Manage your network and introductions"
    >
      {/* Portfolio Growth Hero */}
      <PortfolioGrowthHero
        scope="me"
        label="My Portfolio"
        caption="Your balance from the Native Fund."
      />

      {/* My VC# + Introducer */}
      <div className="mb-6">
        <MyVCCard />
      </div>

      {/* My Introductees — who I brought in + their stage */}
      <div className="mb-6">
        <MyIntroducteesCard />
      </div>

      {/* EchoSense CRM entry point (STARSEED-CRM-011) — dark until the
          crm_referrer_view flag flips (fail-closed; 010-gated). */}
      {isCrmReferrerViewEnabled(user) && (
        <div className="mb-6">
          <Card className="bg-surface border-white/5 card-hover" data-testid="crm-entry-card">
            <CardContent className="p-6 flex items-center justify-between gap-4">
              <div>
                <p className="text-white font-medium font-serif text-lg">My Investors</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your investor book on the EchoSense CRM — stages, capital bands, KYC status, and your notes.
                </p>
              </div>
              <Button
                asChild
                variant="outline"
                className="shrink-0 border-white/10 hover:bg-gold/10 hover:text-gold hover:border-gold/50"
                data-testid="crm-entry-link"
              >
                <Link to="/my-investors">Open</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Card className="bg-surface border-white/5 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="text-gold" size={24} />
            </div>
            <div className="text-3xl font-bold text-slate-900 tabular-nums">{network?.tier1_count || 0}</div>
            <p className="text-sm text-muted-foreground mt-1">Tier 1 Referrals</p>
          </CardContent>
        </Card>

        <Card className="bg-surface border-white/5 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Layers className="text-blue-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-slate-900 tabular-nums">{network?.tier2_count || 0}</div>
            <p className="text-sm text-muted-foreground mt-1">Tier 2 Referrals</p>
          </CardContent>
        </Card>
      </div>

      {/* Referral Code */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        <Card className="bg-surface border-white/5">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <QrCode className="text-gold" size={20} />
              Your Referral Code
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 rounded bg-obsidian border border-white/10 text-center">
                <code className="text-xl font-mono text-gold">{user?.referral_token}</code>
              </div>
              
              <Button 
                onClick={copyReferralCode}
                variant="outline"
                className="w-full border-white/10 hover:bg-gold/10 hover:text-gold hover:border-gold/50"
                data-testid="copy-referral-code"
              >
                {copied ? <CheckCircle2 className="mr-2" size={16} /> : <Copy className="mr-2" size={16} />}
                {copied ? "Copied!" : "Copy Code"}
              </Button>

              <div className="p-3 rounded bg-gold/10 border border-gold/30">
                <p className="text-gold text-sm font-medium">Share your code</p>
                <p className="text-gray-400 text-xs mt-1">
                  Introductions you make are tracked to your referral code
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Network Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tier 1 Referrals */}
        <Card className="bg-surface border-white/5">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gold"></div>
              Tier 1 Network
            </CardTitle>
            <CardDescription>Members you introduced directly</CardDescription>
          </CardHeader>
          <CardContent>
            {network?.tier1_referrals?.length > 0 ? (
              <div className="space-y-3">
                {network.tier1_referrals.map((ref) => (
                  <div key={ref.id} className="flex items-center justify-between p-3 rounded bg-obsidian border border-white/5">
                    <div>
                      <p className="text-white font-medium">{ref.full_name}</p>
                      <p className="text-gray-500 text-sm">{ref.email}</p>
                    </div>
                    <Badge className={ref.kyc_status === "approved" ? "bg-emerald/20 text-emerald" : "bg-orange-500/20 text-orange-400"}>
                      {ref.kyc_status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="mx-auto mb-3 opacity-50" size={40} />
                <p>No Tier 1 referrals yet</p>
                <p className="text-sm mt-1">Share your referral code to grow your network</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tier 2 Referrals */}
        <Card className="bg-surface border-white/5">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-400"></div>
              Tier 2 Network
            </CardTitle>
            <CardDescription>Sign-ups from your Tier 1 network</CardDescription>
          </CardHeader>
          <CardContent>
            {network?.tier2_referrals?.length > 0 ? (
              <div className="space-y-3">
                {network.tier2_referrals.map((ref) => (
                  <div key={ref.id} className="flex items-center justify-between p-3 rounded bg-obsidian border border-white/5">
                    <div>
                      <p className="text-white font-medium">{ref.full_name}</p>
                      <p className="text-gray-500 text-sm">{ref.email}</p>
                    </div>
                    <Badge className={ref.kyc_status === "approved" ? "bg-emerald/20 text-emerald" : "bg-orange-500/20 text-orange-400"}>
                      {ref.kyc_status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Network className="mx-auto mb-3 opacity-50" size={40} />
                <p>No Tier 2 referrals yet</p>
                <p className="text-sm mt-1">Your Tier 1 referrals' signups appear here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ReferrerDashboard;
